import React, { useState, useEffect } from 'react';
import { 
  UserPlus, 
  Users, 
  Camera, 
  CameraOff, 
  Mic, 
  MicOff, 
  Crown, 
  Shield, 
  Video,
  Copy,
  X,
  Eye,
  Settings,
  Volume2,
  VolumeX
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/authService';
import { webrtcService } from '../../services/webrtcService';

function HostManager({ streamId, onHostsChange }) {
  const { user, canManageInvitations } = useAuth();
  const [hosts, setHosts] = useState([]);
  const [pendingHosts, setPendingHosts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addHostForm, setAddHostForm] = useState({
    email: '',
    displayName: '',
    role: 'co-host', // 'co-host', 'guest-host', 'moderator'
    permissions: {
      camera: true,
      microphone: true,
      screenShare: true,
      chat: true,
      viewerInteraction: false,
      streamControl: false,
    }
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadHosts();
    setupWebRTCListeners();
  }, [streamId]);

  const setupWebRTCListeners = () => {
    webrtcService.onHostJoined = (hostInfo) => {
      setHosts(prev => {
        const existing = prev.find(h => h.id === hostInfo.userId);
        if (existing) {
          return prev.map(h => h.id === hostInfo.userId ? 
            { ...h, isOnline: true, lastSeen: new Date() } : h
          );
        }
        return [...prev, {
          id: hostInfo.userId,
          displayName: hostInfo.displayName,
          role: hostInfo.role || 'co-host',
          isOnline: true,
          hasCamera: false,
          hasMicrophone: false,
          isScreenSharing: false,
          lastSeen: new Date(),
          avatar: hostInfo.avatar,
        }];
      });
    };

    webrtcService.onHostLeft = (hostId) => {
      setHosts(prev => prev.map(h => h.id === hostId ? 
        { ...h, isOnline: false, hasCamera: false, hasMicrophone: false, isScreenSharing: false } : h
      ));
    };

    webrtcService.onHostMediaStateChanged = (hostId, mediaState) => {
      setHosts(prev => prev.map(h => h.id === hostId ? 
        { 
          ...h, 
          hasCamera: mediaState.camera,
          hasMicrophone: mediaState.microphone,
          isScreenSharing: mediaState.screenShare
        } : h
      ));
    };
  };

  const loadHosts = async () => {
    try {
      setIsLoading(true);
      // In a real app, this would fetch from the backend
      const mockHosts = [
        {
          id: user?.id,
          displayName: user?.displayName,
          email: user?.email,
          role: 'primary-host',
          isOnline: true,
          hasCamera: false,
          hasMicrophone: false,
          isScreenSharing: false,
          avatar: user?.avatar,
          permissions: {
            camera: true,
            microphone: true,
            screenShare: true,
            chat: true,
            viewerInteraction: true,
            streamControl: true,
          },
          lastSeen: new Date(),
        }
      ];
      setHosts(mockHosts);
      onHostsChange?.(mockHosts);
    } catch (error) {
      console.error('Failed to load hosts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddHost = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      
      // Create invitation for the new host
      const invitation = await authService.createInvitation({
        type: 'stream',
        role: addHostForm.role,
        streamId: streamId,
        description: `Join as ${addHostForm.role} in ${streamId}`,
        maxUses: 1,
        expiresIn: '24h',
        requireApproval: false,
        hostPermissions: addHostForm.permissions,
        inviteeEmail: addHostForm.email,
        inviteeName: addHostForm.displayName,
      });

      // Add to pending hosts
      const newPendingHost = {
        id: `pending-${Date.now()}`,
        displayName: addHostForm.displayName,
        email: addHostForm.email,
        role: addHostForm.role,
        permissions: addHostForm.permissions,
        invitationCode: invitation.code,
        invitedAt: new Date(),
        status: 'pending',
      };

      setPendingHosts(prev => [...prev, newPendingHost]);
      setShowAddModal(false);
      setAddHostForm({
        email: '',
        displayName: '',
        role: 'co-host',
        permissions: {
          camera: true,
          microphone: true,
          screenShare: true,
          chat: true,
          viewerInteraction: false,
          streamControl: false,
        }
      });

      // Send invitation (in real app, this would send email)
      console.log('Host invitation created:', invitation);
    } catch (error) {
      console.error('Failed to add host:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const removeHost = async (hostId) => {
    if (hostId === user?.id) {
      alert("You cannot remove yourself as the primary host");
      return;
    }

    if (window.confirm('Are you sure you want to remove this host?')) {
      setHosts(prev => prev.filter(h => h.id !== hostId));
      // In a real app, would call backend to remove host
      // Also notify via WebRTC that host was removed
      webrtcService.sendHostRemoval?.(hostId);
    }
  };

  const toggleHostPermission = async (hostId, permission) => {
    setHosts(prev => prev.map(h => h.id === hostId ? {
      ...h,
      permissions: {
        ...h.permissions,
        [permission]: !h.permissions[permission]
      }
    } : h));
    // In a real app, would sync with backend
  };

  const copyInvitationLink = (invitationCode) => {
    const inviteUrl = authService.generateInviteUrl(invitationCode, streamId);
    navigator.clipboard.writeText(inviteUrl);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'primary-host':
        return Crown;
      case 'co-host':
        return Users;
      case 'moderator':
        return Shield;
      default:
        return Users;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'primary-host':
        return 'text-yellow-400';
      case 'co-host':
        return 'text-blue-400';
      case 'moderator':
        return 'text-purple-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white mb-2">Stream Hosts</h3>
          <p className="text-gray-400 text-sm">Manage who can broadcast with camera access</p>
        </div>
        
        {canManageInvitations() && (
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <UserPlus className="w-4 h-4" />
            <span>Add Host</span>
          </button>
        )}
      </div>

      {/* Active Hosts */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-white">Active Hosts ({hosts.length})</h4>
        
        {hosts.length === 0 ? (
          <div className="text-center py-8 bg-gray-800 rounded-lg">
            <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No hosts added yet</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {hosts.map(host => {
              const RoleIcon = getRoleIcon(host.role);
              return (
                <div key={host.id} className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <img
                          src={host.avatar || `https://via.placeholder.com/40x40/0051c3/ffffff?text=${host.displayName?.charAt(0) || 'H'}`}
                          alt={host.displayName}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        {host.isOnline && (
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-gray-800 rounded-full"></div>
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h5 className="text-white font-medium">{host.displayName}</h5>
                          <RoleIcon className={`w-4 h-4 ${getRoleColor(host.role)}`} />
                        </div>
                        <p className="text-gray-400 text-sm">{host.email}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`px-2 py-1 rounded-full text-xs ${getRoleColor(host.role)} bg-opacity-20`}>
                            {host.role.replace('-', ' ')}
                          </span>
                          {!host.isOnline && (
                            <span className="px-2 py-1 bg-gray-600 text-gray-300 rounded-full text-xs">
                              Offline
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {/* Media Status */}
                      <div className="flex items-center space-x-1">
                        <button
                          className={`p-1 rounded ${host.hasCamera ? 'bg-green-500 text-white' : 'bg-gray-600 text-gray-400'}`}
                        >
                          {host.hasCamera ? <Camera className="w-4 h-4" /> : <CameraOff className="w-4 h-4" />}
                        </button>
                        <button
                          className={`p-1 rounded ${host.hasMicrophone ? 'bg-green-500 text-white' : 'bg-gray-600 text-gray-400'}`}
                        >
                          {host.hasMicrophone ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
                        </button>
                        {host.isScreenSharing && (
                          <button className="p-1 rounded bg-purple-500 text-white">
                            <Video className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Actions */}
                      {host.id !== user?.id && canManageInvitations() && (
                        <button
                          onClick={() => removeHost(host.id)}
                          className="p-1 text-red-400 hover:text-red-300 rounded hover:bg-red-500 hover:bg-opacity-20"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Permissions */}
                  {host.permissions && (
                    <div className="mt-3 pt-3 border-t border-gray-700">
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
                        {Object.entries(host.permissions).map(([perm, enabled]) => (
                          <div key={perm} className={`flex items-center space-x-1 ${enabled ? 'text-green-400' : 'text-gray-500'}`}>
                            <div className={`w-2 h-2 rounded-full ${enabled ? 'bg-green-400' : 'bg-gray-500'}`}></div>
                            <span className="capitalize">{perm.replace(/([A-Z])/g, ' $1').trim()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pending Invitations */}
      {pendingHosts.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-white">Pending Invitations ({pendingHosts.length})</h4>
          
          <div className="grid gap-3">
            {pendingHosts.map(pending => (
              <div key={pending.id} className="bg-yellow-500 bg-opacity-10 border border-yellow-500 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-white font-medium">{pending.displayName}</h5>
                    <p className="text-gray-400 text-sm">{pending.email}</p>
                    <span className="px-2 py-1 bg-yellow-500 bg-opacity-20 text-yellow-400 rounded-full text-xs">
                      {pending.role.replace('-', ' ')}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => copyInvitationLink(pending.invitationCode)}
                      className="btn-secondary flex items-center space-x-1 text-xs"
                    >
                      <Copy className="w-3 h-3" />
                      <span>Copy Link</span>
                    </button>
                    
                    <button
                      onClick={() => setPendingHosts(prev => prev.filter(p => p.id !== pending.id))}
                      className="p-1 text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Host Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">Add New Host</h3>
            
            <form onSubmit={handleAddHost} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={addHostForm.email}
                  onChange={(e) => setAddHostForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  placeholder="host@example.com"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={addHostForm.displayName}
                  onChange={(e) => setAddHostForm(prev => ({ ...prev, displayName: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  placeholder="Host Name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Host Role
                </label>
                <select
                  value={addHostForm.role}
                  onChange={(e) => setAddHostForm(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                >
                  <option value="co-host">Co-Host</option>
                  <option value="guest-host">Guest Host</option>
                  <option value="moderator">Moderator</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">
                  Permissions
                </label>
                <div className="space-y-2">
                  {Object.entries(addHostForm.permissions).map(([perm, enabled]) => (
                    <label key={perm} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => setAddHostForm(prev => ({
                          ...prev,
                          permissions: {
                            ...prev.permissions,
                            [perm]: e.target.checked
                          }
                        }))}
                        className="rounded"
                      />
                      <span className="text-gray-300 text-sm capitalize">
                        {perm.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary flex-1"
                >
                  {isLoading ? 'Sending Invitation...' : 'Send Invitation'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default HostManager; 